import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { ReAuthModal } from "shared/components/modals/ReAuthModal";
import { setStepUpPrompt, StepUpDismissedError } from "utils/stepUpAuth";

interface PendingPrompt {
  resolve: () => void;
  reject: (reason: Error) => void;
}

/**
 * Owns the app's single OTP prompt and hands it to the step-up interceptor, which
 * needs to collect a code from inside an axios error handler where it cannot render.
 *
 * With this mounted, no call site has to know step-up auth exists: a request refused
 * with `reauth_required` raises the prompt here, and the interceptor replays it.
 */
export const StepUpAuthProvider = ({ children }: { children: ReactNode }) => {
  const [opened, setOpened] = useState(false);
  const pendingRef = useRef<PendingPrompt | null>(null);
  // Concurrent refusals must share one prompt, or the second would orphan the first
  // promise and leave that request hanging forever.
  const inFlightRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    setStepUpPrompt(() => {
      if (inFlightRef.current) return inFlightRef.current;

      inFlightRef.current = new Promise<void>((resolve, reject) => {
        pendingRef.current = { resolve, reject };
        setOpened(true);
      }).finally(() => {
        inFlightRef.current = null;
      });

      return inFlightRef.current;
    });

    return () => {
      setStepUpPrompt(null);
      // Unmounting with the prompt still open — a logout tearing down this subtree,
      // say — would otherwise leave the awaiting request on a promise nothing settles.
      pendingRef.current?.reject(new StepUpDismissedError());
      pendingRef.current = null;
    };
  }, []);

  const settle = useCallback((confirmed: boolean) => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    setOpened(false);

    if (!pending) return;
    // Dismissing rejects, so the original request fails through its own error handling
    // rather than silently never completing.
    if (confirmed) pending.resolve();
    else pending.reject(new StepUpDismissedError());
  }, []);

  return (
    <>
      {children}
      <ReAuthModal
        opened={opened}
        onClose={() => settle(false)}
        onConfirm={() => settle(true)}
      />
    </>
  );
};
